import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, FileDown, ShieldCheck, AlertTriangle, Bell } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

export default function MultiCompanyAuditorConsole() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [jurisdiction, setJurisdiction] = useState<string>('all');
  const [exporting, setExporting] = useState(false);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [trendData, setTrendData] = useState<any[]>([]);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_audit_summaries', {
        p_jurisdiction: jurisdiction === 'all' ? null : jurisdiction,
      });
      if (error) throw error;
      setRows(data || []);

      // Load alerts
      const { data: alertsData } = await supabase
        .from('audit_alerts')
        .select('*, companies(name)')
        .eq('acknowledged', false)
        .order('created_at', { ascending: false });
      setAlerts(alertsData || []);

      // Log view
      await supabase.from('audit_log').insert({
        user_id: (await supabase.auth.getUser()).data.user?.id,
        action: 'AUDIT_SUMMARY_VIEWED',
        entity_type: 'audit_summary',
        entity_id: crypto.randomUUID(),
        details: { company_count: (data || []).length },
      });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadTrend(companyId: string) {
    try {
      const { data, error } = await supabase.rpc('get_company_audit_trend', {
        p_company_id: companyId,
        p_days: 90,
      });
      if (error) throw error;
      setTrendData(data || []);
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function acknowledgeAlert(alertId: string) {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { error } = await supabase
        .from('audit_alerts')
        .update({
          acknowledged: true,
          acknowledged_by: userId,
          acknowledged_at: new Date().toISOString(),
        })
        .eq('id', alertId);
      if (error) throw error;
      toast.success('Alert acknowledged');
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function exportPdf() {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-audit-summary-pdf', {
        body: { filters: { search: filter } },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
        toast.success('Report exported');
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    load();
  }, [jurisdiction]);

  const filtered = rows.filter(
    (r) => !filter || r.company_name?.toLowerCase().includes(filter.toLowerCase())
  );

  const jurisdictions = [
    'all',
    ...Array.from(new Set(rows.map((r) => r.jurisdiction).filter(Boolean))),
  ];

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="animate-spin text-muted-foreground h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <ShieldCheck className="text-primary" /> Multi-Company Auditor Console
          {alerts.length > 0 && (
            <Badge variant="destructive" className="ml-2">
              <Bell className="h-3 w-3 mr-1" />
              {alerts.length}
            </Badge>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Select value={jurisdiction} onValueChange={setJurisdiction}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Jurisdictions" />
            </SelectTrigger>
            <SelectContent>
              {jurisdictions.map((j) => (
                <SelectItem key={j} value={j}>
                  {j === 'all' ? 'All Jurisdictions' : j}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Search company…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-56"
          />
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={exportPdf} disabled={exporting}>
            {exporting ? (
              <Loader2 className="animate-spin h-4 w-4" />
            ) : (
              <FileDown className="h-4 w-4 mr-1" />
            )}
            Export PDF
          </Button>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className="border-amber-500 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <AlertTriangle className="h-5 w-5" />
              Active Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.slice(0, 5).map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-3 bg-background rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">{alert.companies?.name}</div>
                  <div className="text-sm text-muted-foreground">{alert.message}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(alert.created_at).toLocaleString()}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => acknowledgeAlert(alert.id)}>
                  Acknowledge
                </Button>
              </div>
            ))}
            {alerts.length > 5 && (
              <div className="text-sm text-center text-muted-foreground">
                ...and {alerts.length - 5} more alerts
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Overview chart */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>Trust Scores by Company</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filtered.slice(0, 20)}>
              <XAxis dataKey="company_name" hide />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="trust_score" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Summary stats */}
      <div className="grid sm:grid-cols-4 gap-3">
        <Card className="border-border bg-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Total Companies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{filtered.length}</div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Avg Trust Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {filtered.length > 0
                ? Math.round(
                    filtered.reduce((sum, c) => sum + (c.trust_score || 0), 0) / filtered.length
                  )
                : 0}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Companies w/ Flags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {filtered.filter((c) => (c.flags || 0) > 0).length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Avg Proof Coverage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {filtered.length > 0
                ? Math.round(
                    filtered.reduce((sum, c) => sum + (c.proof_coverage || 0), 0) / filtered.length
                  )
                : 0}
              %
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Historical Trend View */}
      {selectedCompany && trendData.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Trust Score Trend (90 Days)</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <XAxis dataKey="audit_date" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="trust_score"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  name="Trust Score"
                />
                <Line
                  type="monotone"
                  dataKey="risk_score"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2}
                  name="Risk Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>Audit Summary</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {filtered.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead>Last Audit</TableHead>
                  <TableHead>Trust Score</TableHead>
                  <TableHead>Proof Coverage</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead>Risk Score</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={r.company_id}
                    className={selectedCompany === r.company_id ? 'bg-muted/50' : ''}
                  >
                    <TableCell className="font-medium">{r.company_name}</TableCell>
                    <TableCell>{r.jurisdiction || '—'}</TableCell>
                    <TableCell>
                      {r.last_audit_at ? new Date(r.last_audit_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>{r.trust_score ?? '—'}</TableCell>
                    <TableCell>{r.proof_coverage ? `${r.proof_coverage}%` : '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {r.flags ?? 0}
                        {r.flags > 0 && <AlertTriangle className="text-amber-500 h-4 w-4" />}
                      </div>
                    </TableCell>
                    <TableCell>{r.risk_score ?? '—'}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCompany(r.company_id);
                          loadTrend(r.company_id);
                        }}
                      >
                        Trend
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/compliance/audit-console?company=${r.company_id}`)
                        }
                      >
                        Detail
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No companies found{filter ? ' matching your search' : ''}.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
